import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { ImportService } from 'services/imports/import.service';
import { Card, CardHeader } from 'components/common/Card';
import { Button } from 'components/common/Button';
import { StatusBadge } from 'components/common/Badge';
import { LoadingScreen } from 'components/common/Spinner';
import { Pagination } from 'components/common/Pagination';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Import, ImportDetection, ImportType } from 'dtos/imports/import.dto';
import { ADVANCED_IMPORTS, ALL_IMPORTS, FIELD_LABELS, ImportTypeInfo, importTypeLabel, PRIMARY_IMPORTS, templateCsv } from 'constants/importTypes';
import { formatDateTime } from 'utils/formatters';
import { toUploadableFile, UPLOAD_ACCEPT } from 'utils/spreadsheet';

const ACTIVE_STATUSES = ['UPLOADED', 'READY', 'VALIDATING', 'PROCESSING'];

function downloadTemplate(info: ImportTypeInfo) {
  const url = URL.createObjectURL(new Blob([templateCsv(info)], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url; link.download = `pellvr-${info.type.toLowerCase().replace(/_/g, '-')}-template.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function ImportRow({ imp }: { imp: Import }) {
  const [open, setOpen] = useState(false);
  const Icon = imp.status === 'COMPLETED' ? CheckCircle : imp.status === 'FAILED' ? XCircle : imp.status === 'PARTIALLY_COMPLETED' ? AlertTriangle : FileText;
  const color = imp.status === 'COMPLETED' ? 'text-green-500' : imp.status === 'FAILED' ? 'text-red-500' : imp.status === 'PARTIALLY_COMPLETED' ? 'text-orange-500' : 'text-gray-400';
  const errors = imp.validation_errors ?? [];
  return (
    <div className="border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-4 px-5 py-3">
        <Icon size={18} className={`flex-shrink-0 ${color}`} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900">{imp.file_name}</p>
          <p className="text-xs text-gray-500">{importTypeLabel(imp.import_type)} · {imp.store_name ?? 'All stores'} · {formatDateTime(imp.created_at)}</p>
          <p className="text-xs text-gray-500">
            {imp.status === 'COMPLETED' && `${imp.successful_rows.toLocaleString()} rows imported`}
            {ACTIVE_STATUSES.includes(imp.status) && 'Processing — this page updates automatically'}
            {imp.failed_rows > 0 && <span className="text-red-600"> · {imp.failed_rows.toLocaleString()} rows skipped</span>}
            {imp.error_message && <span className="text-red-600"> · {imp.error_message}</span>}
          </p>
        </div>
        {errors.length > 0 && (
          <button onClick={() => setOpen(value => !value)} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
            Details {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
        <StatusBadge status={imp.status} />
      </div>
      {open && (
        <ul className="space-y-1 bg-gray-50 px-5 py-3 text-xs text-gray-700">
          {errors.slice(0, 10).map((error: any, index) => { const field = error.field ?? error.column_name; return <li key={index}>Row {error.row ?? error.row_number}: {error.problem ?? error.error_message}{field ? ` (${FIELD_LABELS[field] ?? field})` : ''}</li>; })}
          {errors.length > 10 && <li className="text-gray-500">…and {errors.length - 10} more</li>}
        </ul>
      )}
    </div>
  );
}

function TypeCard({ info, onPick }: { info: ImportTypeInfo; onPick: () => void }) {
  return (
    <div className="flex flex-col rounded-lg border border-gray-200 p-4">
      <p className="text-sm font-semibold text-gray-900">{info.title}</p>
      <p className="mt-1 flex-1 text-sm text-gray-600">{info.description}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={onPick}><Upload size={14} /> Upload</Button>
        <Button size="sm" variant="ghost" onClick={() => downloadTemplate(info)}><Download size={14} /> Template</Button>
      </div>
    </div>
  );
}

function MappingReview({ info, detection, onCancel, onConfirm, busy }: { info: ImportTypeInfo; detection: ImportDetection; onCancel: () => void; onConfirm: (mapping: Record<string, string>) => void; busy: boolean }) {
  const fields = [...detection.required_fields, ...(detection.optional_fields ?? info.columns.filter(column => !detection.required_fields.includes(column)))];
  const [mapping, setMapping] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = { ...detection.mapping };
    for (const field of fields) if (!initial[field] && detection.headers.includes(field)) initial[field] = field;
    return initial;
  });
  const alternatives = detection.field_alternatives ?? {};
  const missing = detection.required_fields.filter(field => !mapping[field] && !(alternatives[field] ?? []).some(alternative => mapping[alternative]));
  const mappedFields = fields.filter(field => mapping[field]);
  // When every required column was found, the owner only needs to glance at
  // the preview; the full column list stays one click away.
  const [editing, setEditing] = useState(missing.length > 0);

  return (
    <div className="space-y-4">
      {editing ? (
        <>
          <p className="text-sm text-gray-600">Match your columns. Fields marked * are needed.</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {fields.map(field => {
              const required = detection.required_fields.includes(field);
              return (
                <label key={field} className="text-sm">
                  <span className="font-medium text-gray-700">{FIELD_LABELS[field] ?? field}{required && <span className="text-red-600"> *</span>}{required && alternatives[field]?.length ? <span className="font-normal text-gray-400"> (or {alternatives[field].map(alt => FIELD_LABELS[alt] ?? alt).join(' / ')})</span> : null}</span>
                  <select value={mapping[field] ?? ''} onChange={event => setMapping(current => ({ ...current, [field]: event.target.value }))}
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${missing.includes(field) ? 'border-red-300' : 'border-gray-300'}`}>
                    <option value="">{required ? 'Choose a column…' : '(not in my file)'}</option>
                    {detection.headers.map(header => <option key={header} value={header}>{header}</option>)}
                  </select>
                </label>
              );
            })}
          </div>
        </>
      ) : (
        <p className="flex flex-wrap items-center gap-x-2 text-sm text-gray-700">
          <CheckCircle size={16} className="text-green-600" />
          Found {mappedFields.map(field => FIELD_LABELS[field] ?? field).join(', ')}.
          <button type="button" onClick={() => setEditing(true)} className="text-blue-600 hover:underline">Change columns</button>
        </p>
      )}
      {detection.preview.length > 0 && mappedFields.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="w-full text-xs">
            <thead className="bg-gray-50"><tr>{mappedFields.map(field => <th key={field} className="px-3 py-2 text-left font-semibold text-gray-600">{FIELD_LABELS[field] ?? field}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-100">
              {detection.preview.slice(0, 3).map((row, index) => <tr key={index}>{mappedFields.map(field => <td key={field} className="whitespace-nowrap px-3 py-2 text-gray-700">{row[mapping[field]] ?? ''}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
      )}
      {missing.length > 0 && <p className="text-sm text-red-600">Choose a column for: {missing.map(field => FIELD_LABELS[field] ?? field).join(', ')}.</p>}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={() => onConfirm(Object.fromEntries(Object.entries(mapping).filter(([, header]) => header)))} disabled={missing.length > 0} loading={busy}>
          {detection.row_count ? `Import ${detection.row_count.toLocaleString()} rows` : 'Start import'}
        </Button>
      </div>
    </div>
  );
}

function XmlReview({ detection, onCancel, onConfirm, busy }: { detection: ImportDetection; onCancel: () => void; onConfirm: () => void; busy: boolean }) {
  const summary = detection.summary;
  const dates = summary?.first_date ? (summary.first_date === summary.last_date ? summary.first_date : `${summary.first_date} to ${summary.last_date}`) : null;
  return (
    <div className="space-y-4">
      <p className="flex flex-wrap items-center gap-x-2 text-sm text-gray-700">
        <CheckCircle size={16} className="text-green-600" />
        {(summary?.items ?? 0).toLocaleString()} items{summary?.sales_lines ? `, ${summary.sales_lines.toLocaleString()} item sales lines` : ''}{dates ? ` (${dates})` : ''}. No column setup needed.
      </p>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button onClick={onConfirm} loading={busy}>Start import</Button>
      </div>
    </div>
  );
}

// One flow for both entry points: `info` set means the owner picked the file
// type; `info` null means "drop anything" and the backend decides the type.
function ImportFlow({ info, onClose }: { info: ImportTypeInfo | null; onClose: (started: boolean) => void }) {
  const { org, currentStore } = useStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<{ importId: string; fileName: string; detection: ImportDetection; type: ImportType } | null>(null);
  const [dragging, setDragging] = useState(false);
  const auto = info === null;
  const pendingInfo = pending ? ALL_IMPORTS.find(item => item.type === pending.type) ?? info : null;

  const pick = async (file: File | undefined) => {
    if (!file || !org) return;
    const storeId = info?.scope === 'organization' ? null : currentStore?.id ?? null;
    if (auto && !storeId) { setError('Choose a store first, then drop the file again.'); return; }
    setBusy(true); setError('');
    try {
      const upload = await toUploadableFile(file);
      const type = info?.type ?? 'CATALOG';
      const { importRecord, detection } = await ImportService.upload(org.id, upload, storeId, type, auto);
      setPending({ importId: importRecord.id, fileName: file.name, detection, type: detection.import_type ?? type });
    } catch (err: any) { setError(err?.message || 'Upload failed. Please try again.'); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const retype = async (type: ImportType) => {
    if (!org || !pending) return;
    setBusy(true); setError('');
    try { const detection = await ImportService.retype(org.id, pending.importId, type); setPending({ ...pending, detection, type: detection.import_type ?? type }); }
    catch (err: any) { setError(err?.message || 'Could not switch the file type.'); }
    finally { setBusy(false); }
  };

  const confirm = async (mapping: Record<string, string>) => {
    if (!org || !pending) return;
    setBusy(true); setError('');
    try { await ImportService.confirm(org.id, pending.importId, mapping); onClose(true); }
    catch (err: any) { setError(err?.message || 'The import could not be started.'); setBusy(false); }
  };

  const title = info ? `Import ${info.title.toLowerCase()}` : 'Drop any file';
  const subtitle = info?.scope === 'organization' ? 'For all stores' : `Into ${currentStore?.name ?? 'your store'}`;
  const typeChoices = PRIMARY_IMPORTS.filter(item => pending?.detection.format !== 'naxml' || item.type !== 'SUPPLIER_COSTS');
  return (
    <Card>
      <CardHeader title={pending ? pending.fileName : title} subtitle={subtitle} />
      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {pending && pendingInfo ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {typeChoices.some(item => item.type === pending.type) ? (
              <label className="flex items-center gap-2 text-gray-700">This file is
                <select value={pending.type} disabled={busy} onChange={event => retype(event.target.value as ImportType)} className="rounded-md border border-gray-300 px-2 py-1 text-sm">
                  {typeChoices.map(item => <option key={item.type} value={item.type}>{item.title}</option>)}
                </select>
              </label>
            ) : <span className="text-gray-700">{pendingInfo.title}</span>}
            {pending.detection.source && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">Recognized: {pending.detection.source.label}</span>}
          </div>
          {pending.detection.format === 'naxml'
            ? <XmlReview detection={pending.detection} busy={busy} onCancel={() => onClose(false)} onConfirm={() => confirm({})} />
            : <MappingReview key={pending.type} info={pendingInfo} detection={pending.detection} busy={busy} onCancel={() => onClose(false)} onConfirm={confirm} />}
        </div>
      ) : (
        <div className="space-y-4">
          <button type="button" disabled={busy} onClick={() => inputRef.current?.click()}
            onDragOver={event => { event.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
            onDrop={event => { event.preventDefault(); setDragging(false); void pick(event.dataTransfer.files?.[0]); }}
            className={`w-full rounded-lg border-2 border-dashed p-8 text-center transition-colors hover:border-blue-400 hover:bg-blue-50 ${dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
            <Upload size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-medium text-gray-800">{busy ? 'Reading your file…' : auto ? 'Drop your price book, item sales report or vendor cost file here' : 'Drop the file here, or click to choose one'}</p>
            <p className="mt-1 text-xs text-gray-500">
              CSV, Excel (.xlsx) or POS XML, as it comes from your POS, back office or wholesaler.{' '}
              {auto ? 'Pellvr works out what is in it; you confirm before anything is imported.' : 'Column names don’t have to match; you confirm them next.'}
            </p>
          </button>
          <input ref={inputRef} type="file" accept={UPLOAD_ACCEPT} className="hidden" onChange={event => pick(event.target.files?.[0])} />
          {info && (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => onClose(false)} disabled={busy}>Cancel</Button>
              <Button variant="ghost" onClick={() => downloadTemplate(info)}><Download size={14} /> Download template</Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function ImportsPage() {
  const { org } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = [...PRIMARY_IMPORTS, ...ADVANCED_IMPORTS].find(info => info.type === searchParams.get('type')) ?? null;
  const [active, setActive] = useState<ImportTypeInfo | null>(requested);
  const [flowKey, setFlowKey] = useState(0);
  const [started, setStarted] = useState(false);
  const [showTypes, setShowTypes] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [page, setPage] = useState(1);
  const { data: imports, loading, refetch } = useApi(() => ImportService.list(org?.id ?? '', page, 8), [org?.id, page]);

  const processing = imports?.data.some(imp => ACTIVE_STATUSES.includes(imp.status)) ?? false;
  useEffect(() => {
    if (!processing) return;
    const timer = setInterval(refetch, 5000);
    return () => clearInterval(timer);
  }, [processing, refetch]);

  const finish = (didStart: boolean) => {
    setActive(null); setFlowKey(key => key + 1); setStarted(didStart);
    if (searchParams.has('type')) setSearchParams({}, { replace: true });
    if (didStart) refetch();
  };
  const pickType = (info: ImportTypeInfo) => { setStarted(false); setActive(info); };

  if (loading && !imports) return <LoadingScreen />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Upload data</h1>
        <p className="text-sm text-gray-500">Drop the files your POS, back office or wholesaler already gives you. No reformatting: Pellvr reads CSV, Excel and POS XML and recognizes what each file is.</p>
      </div>

      {started && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <CheckCircle size={16} className="flex-shrink-0" /> Import started. It usually takes about a minute and the history below updates on its own. You can drop the next file now.
        </div>
      )}

      {active ? <ImportFlow info={active} onClose={finish} /> : <ImportFlow key={flowKey} info={null} onClose={finish} />}

      {!active && (
        <Card>
          <button onClick={() => setShowTypes(value => !value)} className="flex w-full items-center justify-between text-sm text-gray-700 hover:text-gray-900">
            <span>Prefer to choose the file type, or need a template?</span>
            {showTypes ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showTypes && (
            <>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                {PRIMARY_IMPORTS.map(info => <TypeCard key={info.type} info={info} onPick={() => pickType(info)} />)}
              </div>
              <button onClick={() => setShowAdvanced(value => !value)} className="mt-4 flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
                Other imports {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showAdvanced && (
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                  {ADVANCED_IMPORTS.map(info => <TypeCard key={info.type} info={info} onPick={() => pickType(info)} />)}
                </div>
              )}
            </>
          )}
        </Card>
      )}

      <Card padding={false}>
        <div className="border-b border-gray-100 px-5 py-3"><h3 className="text-sm font-semibold text-gray-900">Import history</h3></div>
        {!imports?.data.length ? (
          <div className="py-12 text-center text-sm text-gray-500">No imports yet. Start with your price book (item list with shelf prices).</div>
        ) : (
          imports.data.map(imp => <ImportRow key={imp.id} imp={imp} />)
        )}
        {imports && <Pagination page={page} pageSize={8} total={imports.meta.total} onPageChange={setPage} />}
      </Card>
    </div>
  );
}
